import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { FleetTrailer } from '../models/trailer.model';
import { FleetTrailerService } from '../trailer.service';

@Component({
    selector: 'app-trailer-view',
    standalone: true,
    templateUrl: './trailer-view.component.html',
    styleUrls: ['../trailer-page.css'],
    imports: [
        CommonModule,
        RouterModule,
        BreadcrumbModule,
        ButtonModule,
        TagModule,
        ToastModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ]
})
export class TrailerViewComponent implements OnInit {
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };
    trailerId = 0;
    trailer?: FleetTrailer;
    isLoading = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private trailerService: FleetTrailerService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.trailerId = Number(this.route.snapshot.paramMap.get('id'));
        this.breadcrumbItems = [
            { label: 'Fleet', routerLink: '/fleet' },
            { label: 'Trailers', routerLink: '/fleet/trailers' },
            { label: 'View' }
        ];
        if (this.trailerId) {
            this.load();
        }
    }

    load(): void {
        this.isLoading = true;
        this.trailerService.getTrailer(this.trailerId).subscribe({
            next: response => {
                this.trailer = response.data;
                this.isLoading = false;
            },
            error: () => {
                this.isLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load trailer.' });
            }
        });
    }

    edit(): void {
        this.router.navigate(['/fleet/trailer/edit', this.trailerId]);
    }

    back(): void {
        this.router.navigate(['/fleet/trailers']);
    }

    metrics(): PageMetric[] {
        return [
            { label: 'Status', value: this.trailer?.status || '-', hint: 'Operational availability', tone: this.statusTone() },
            { label: 'Active', value: this.trailer?.active === false ? 'Inactive' : 'Active', hint: 'Fleet asset flag', tone: this.trailer?.active === false ? 'danger' : 'success' },
            { label: 'Type', value: this.trailer?.trailerTypeName || '-', hint: this.trailer?.trailerTypeCode || '-', tone: 'info' },
            { label: 'Registration', value: this.trailer?.registrationNumber || '-', hint: this.dateLabel(this.trailer?.registrationExpiry), tone: 'default' }
        ];
    }

    statusSeverity(status?: string): 'success' | 'info' | 'warning' | 'danger' | 'secondary' {
        switch (status) {
            case 'AVAILABLE':
                return 'success';
            case 'ASSIGNED':
                return 'info';
            case 'MAINTENANCE':
                return 'warning';
            case 'OUT_OF_SERVICE':
            case 'INACTIVE':
                return 'danger';
            default:
                return 'secondary';
        }
    }

    activeSeverity(active?: boolean): 'success' | 'danger' {
        return active === false ? 'danger' : 'success';
    }

    dateLabel(value?: string | Date): string {
        return value ? formatDate(value, 'MMM d, y', 'en-US') : '-';
    }

    private statusTone(): PageMetric['tone'] {
        switch (this.trailer?.status) {
            case 'AVAILABLE':
                return 'success';
            case 'ASSIGNED':
                return 'info';
            case 'MAINTENANCE':
                return 'warn';
            case 'OUT_OF_SERVICE':
            case 'INACTIVE':
                return 'danger';
            default:
                return 'default';
        }
    }
}
